import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Integration, IntegrationType, useIntegrationsQuery } from '../../graphql/types';
import { debounce, delay } from 'lodash';
import { Autocomplete, ClickAwayListener, Stack, TextField } from '@mui/material';
import AssetLogo from '../AssetLogo';
import AssetListButtonOption from '../AssetListButtonOption';
import { useFormik } from 'formik';
import { Values } from '../merchants/AddMerchantCashAdvance';
import { RecurrencyValues } from '../merchants/AddRecurrence';
import { AssignRecurrencyValues } from '../merchants/AssignExitingRecurrence';

export interface Props {
  id: string;
  disabled: boolean;
  formik: ReturnType<typeof useFormik<Values> | typeof useFormik<RecurrencyValues> | typeof useFormik<AssignRecurrencyValues>>;
  availableTypes?: IntegrationType[];
  onValueChanged?: (integration: Integration | null) => void;
}

export const IntegrationAutocomplete = ({ id, disabled, formik, availableTypes, onValueChanged }: Props) => {
  const { errors, touched, values, handleBlur, setFieldValue } = formik;
  const [showInfo, setShowInfo] = useState<boolean>(disabled ? true : !!values?.integration?.id);
  const inputReference = useRef<HTMLInputElement>(null);
  const [integrations, setIntegrations] = useState<readonly Integration[]>([]);
  const [integrationInputValue, setIntegrationInputValue] = useState<string>('');
  const {
    loading: integrationsLoading,
    data: integrationsData,
    refetch: integrationsRefetch
  } = useIntegrationsQuery({
    variables: {},
    fetchPolicy: 'network-only',
    skip: disabled
  });
  const integrationsFetch = useMemo(
    () =>
      debounce(
        (request: { input: string }) =>
          integrationsRefetch({}).then((rq) => setIntegrations((rq.data.integrations.data as Integration[]) || [])),
        200
      ),
    [integrationsRefetch]
  );
  useEffect(
    () =>
      setIntegrations(
        ((integrationsData?.integrations.data as Integration[]) || []).filter((integration) => {
          if (availableTypes?.length) {
            return integration.autostart && availableTypes.includes(integration.type);
          }
          return integration.autostart;
        })
      ),
    [integrationsData?.integrations.data, integrationsLoading]
  );

  useEffect(() => {
    setShowInfo(disabled || !!values?.integration?.id);
  }, [disabled, values?.integration?.id]);

  /*  useEffect(() => {
    integrationsFetch({ input: integrationInputValue });
  }, [integrationInputValue, integrationsFetch]);*/

  return (
    <>
      {values?.integration?.id && showInfo && (
        <Stack direction={'row'}>
          <AssetListButtonOption
            logo={values?.integration?.logo || null}
            logo2={values?.integration?.company?.logo || null}
            name={values?.integration?.name || ''}
            type="Platform"
            showEditAction={!disabled}
            onClick={() => {
              if (disabled) return;
              setShowInfo(false);
              delay(() => inputReference.current?.focus(), 500);
            }}
          />
        </Stack>
      )}
      {!showInfo && (
        <ClickAwayListener
          onClickAway={() => {
            setShowInfo(!disabled ? !!values?.integration?.id : false);
          }}
        >
          <Autocomplete
            disabled={disabled}
            id={id}
            getOptionLabel={(option: Integration) => option.name || ''}
            options={integrations}
            autoComplete
            value={(values.integration?.id ? values.integration as Integration : null)}
            isOptionEqualToValue={(option: Integration, value: Integration) =>
              !!value?.id && option.id === value.id
            }
            onChange={(event: SyntheticEvent, newValue: Integration | null) => {
              setFieldValue('integration', newValue || null);
              setShowInfo(!!newValue?.id);
              if (onValueChanged) {
                onValueChanged(newValue);
              }
            }}
            onInputChange={(event: SyntheticEvent, newInputValue: string) => {
              setIntegrationInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={'Select Platform'}
                inputRef={inputReference}
                name="integration"
                onBlur={handleBlur}
                error={Boolean(
                  (touched.integration && errors.integration) ||
                  (touched.integration?.id && errors.integration?.id)
                )}
                helperText={
                  (errors.integration || errors.integration?.id) &&
                  (touched.integration || touched.integration?.id)
                    ? String(errors.integration?.id || errors.integration)
                    : ''
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: values?.integration?.id && (
                    <AssetLogo logo={values?.integration?.logo} name={values?.integration?.name} size={'sm'} isPublic={true}/>
                  )
                }}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& .MuiInputLabel-root': { padding: '12px 0 12px 16px' },
                  '& .MuiOutlinedInput-root': { padding: '12px 0 12px 16px' }
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <AssetListButtonOption
                  logo={option.logo || null}
                  logo2={option?.company?.logo || undefined}
                  name={option.name || ''}
                  type="Platform"
                  {...rest}
                  key={key}
                />
              );
            }}
          />
        </ClickAwayListener>
      )}
    </>
  );
};
